import { useRef, useCallback, useEffect, useState } from 'react';

const useAudioAlert = ({ enabled = true, volume = 0.7 } = {}) => {
  const audioContextRef = useRef(null);
  const [soundReady, setSoundReady] = useState(false);

  // Browsers keep an AudioContext 'suspended' until a user gesture, which
  // would silently drop the first new-order beep. Create + resume it here.
  const ensureContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      audioContextRef.current = new AudioCtx();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume()
        .then(() => setSoundReady(ctx.state === 'running'))
        .catch(() => {});
    } else {
      setSoundReady(ctx.state === 'running');
    }
    return ctx;
  }, []);

  // Unlock audio on the cook's first interaction with the page.
  useEffect(() => {
    const unlock = () => ensureContext();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [ensureContext]);

  // Returns true if the beep actually played, false if audio is unavailable
  // or still blocked — the caller can surface that to the user.
  const playSound = useCallback(() => {
    if (!enabled) return false;
    try {
      const ctx = ensureContext();
      if (!ctx || ctx.state !== 'running') return false;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(volume * 0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
      return true;
    } catch (err) {
      console.error('Error playing alert sound:', err);
      return false;
    }
  }, [enabled, volume, ensureContext]);

  const stopSound = useCallback(() => {
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (_) {}
      audioContextRef.current = null;
      setSoundReady(false);
    }
  }, []);

  return { playSound, stopSound, soundReady };
};

export default useAudioAlert;
